import React from 'react';
import { METODOS_CIERRE, LABELS_METODO_CIERRE, type BalanceByMethod } from '../../lib/cierre/types';
import { formatCOP, totalBalance } from '../../lib/cierre/calculations';

interface BalanceGridProps {
  balances: BalanceByMethod;
  label?: string;
  showZero?: boolean;
  /** Cuando se muestran 4 columnas: inicial, ingresos, gastos, final */
  columns?: 'simple' | 'cierre';
  inicial?: BalanceByMethod;
  ingresos?: BalanceByMethod;
  gastos?: BalanceByMethod;
  highlightNegative?: boolean;
}

export const BalanceGrid: React.FC<BalanceGridProps> = ({
  balances,
  label,
  showZero = true,
  columns = 'simple',
  inicial,
  ingresos,
  gastos,
  highlightNegative,
}) => {
  if (columns === 'cierre' && inicial && ingresos && gastos) {
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {label ? <p className="border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-700">{label}</p> : null}
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
              <th className="px-3 py-2">Método</th>
              <th className="px-3 py-2 text-right">Inicial</th>
              <th className="px-3 py-2 text-right">+Ingresos</th>
              <th className="px-3 py-2 text-right">−Gastos</th>
              <th className="px-3 py-2 text-right">= Final</th>
            </tr>
          </thead>
          <tbody>
            {METODOS_CIERRE.map((m) => {
              const fin = balances[m];
              const show =
                showZero ||
                inicial[m] !== 0 ||
                ingresos[m] !== 0 ||
                gastos[m] !== 0 ||
                fin !== 0;
              if (!show) return null;
              const neg = highlightNegative && fin < 0;
              return (
                <tr
                  key={m}
                  className={`border-b border-gray-50 ${neg ? 'bg-red-50' : ''} ${fin === 0 && !neg ? 'text-gray-400' : 'text-gray-900'}`}
                >
                  <td className="px-3 py-2 font-medium">{LABELS_METODO_CIERRE[m]}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCOP(inicial[m])}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatCOP(ingresos[m])}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-800">{formatCOP(gastos[m])}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{formatCOP(fin)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCOP(totalBalance(inicial))}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCOP(totalBalance(ingresos))}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCOP(totalBalance(gastos))}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCOP(totalBalance(balances))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      {label ? <p className="border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-700">{label}</p> : null}
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
            <th className="px-3 py-2">Método</th>
            <th className="px-3 py-2 text-right">Monto</th>
          </tr>
        </thead>
        <tbody>
          {METODOS_CIERRE.map((m) => {
            const v = balances[m];
            if (!showZero && v === 0) return null;
            return (
              <tr key={m} className={`border-b border-gray-50 ${v === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                <td className="px-3 py-2 font-medium">{LABELS_METODO_CIERRE[m]}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatCOP(v)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-50 font-semibold">
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-right tabular-nums">{formatCOP(totalBalance(balances))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
